import React, { useEffect, useRef } from "react";
import dotPropImmutable from "dot-prop-immutable";
import { cloneDeep } from "lodash-es";
import { ChartMaker } from "../../../utils/ChartMaker";
import utils from "../../../utils/Utils";
import NudgeIndexer from "../../../utils/NudgeIndexer";
import { notify } from "~/components/Notifications";
import { useChartEditContext } from "./ChartEditContext";
import { FaPlus, FaMinus, FaScissors, FaCopy, FaArrowUp, FaArrowDown, FaRotateLeft, FaRotateRight } from "react-icons/fa6";

export default function ChartEditButtons() {
  const {
    chart,
    editedChart,
    edit_location,
    btnClass,
    setEditedChart,
    setActiveBeat,
    historyGo,
    historyIsAtStart,
    historyIsAtEnd,
    historyNumItems,
  } = useChartEditContext();
  // Path helpers — all assume edit_location is non-null
  function parsedPath(): (string | number)[] {
    const p: (string | number)[] = edit_location!.split(".");
    [1, 3, 5].forEach((i) => (p[i] = parseInt(p[i] as string))); // "1" -> 1
    return p;
  }

  const sectionsPath = () => parsedPath().slice(0, 1).join("."); // i.e. 'chords'
  const sectionPath = () => parsedPath().slice(0, 2).join("."); // i.e. 'chords.1'
  const sectionI = () => parsedPath()[1] as number;
  const linesPath = () => parsedPath().slice(0, 3).join("."); // i.e. 'chords.1.lines'
  const linePath = () => parsedPath().slice(0, 4).join("."); // i.e. 'chords.1.lines.1'
  const lineI = () => parsedPath()[3] as number;
  const measurePath = () => parsedPath().slice(0, 6).join("."); // i.e. 'chords.1.lines.1.measures.1'
  const measureI = () => parsedPath()[5] as number;
  const beatI = () => parsedPath()[7];

  // Stable ref so the keydown handler registered at mount always calls latest functions
  const handlerRef = useRef({ breakLine: () => { }, duplicateLine: () => { } });

  useEffect(() => {
    handlerRef.current = { breakLine, duplicateLine };
  });

  useEffect(() => {
    const handler = (ev: KeyboardEvent) => {
      console.log(ev);
      if (ev.key === "x" && utils.isMeta(ev)) handlerRef.current.breakLine();
      if (ev.key === "d" && utils.isMeta(ev)) handlerRef.current.duplicateLine();
      if (ev.key === "u" && utils.isMeta(ev)) historyGo("back");
      if (ev.key === "z" && utils.isMeta(ev)) historyGo("back");
      if (ev.key === "y" && utils.isMeta(ev)) historyGo("forward");
    };
    document.addEventListener("keydown", handler);
    return () => document.removeEventListener("keydown", handler);
  }, []);

  function addBeat() {
    const p = parsedPath();
    const i = p.pop() as number;
    setEditedChart(draft => {
      dotPropImmutable.get(draft, p.join(".")).splice(i, 0, "");
    });
  }

  function removeBeat() {
    const p = parsedPath();
    const i = p.pop() as number;
    setEditedChart(draft => {
      dotPropImmutable.get(draft, p.join(".")).splice(i, 1);
    });
  }

  function addMeasure() {
    const p = parsedPath();
    const measures = dotPropImmutable.get(editedChart, p.slice(0, 5));
    const new_measure =
      measures[measures.length - 1] ||
      new ChartMaker().newChordSection().lines[0].measures[0];
    setEditedChart(draft => {
      dotPropImmutable.get(draft, p.slice(0, 5)).push(new_measure);
    });
  }

  function removeMeasure() {
    const p = parsedPath();
    setEditedChart(draft => {
      dotPropImmutable.get(draft, p.slice(0, 5)).splice(p[5] as number, 1);
    });
    p[5] = Math.max(p[5] as number - 1, 0); // measure before or first
    setActiveBeat(p.join("."));
  }

  // adds line to section after current line, duplicating it
  function addLine() {
    const p = parsedPath();
    const line = cloneDeep(editedChart.chords[p[1] as number].lines[p[3] as number]);
    setEditedChart(draft => {
      draft.chords[p[1] as number].lines.splice(p[3] as number + 1, 0, line);
    });
  }

  function removeLine() {
    const p = parsedPath();
    const prev_path = cloneDeep(p);
    prev_path[3] = Math.max(p[3] as number - 1, 0); // line before or first

    if (dotPropImmutable.get(editedChart, linesPath()).length === 1) return; // don't delete last remaining line

    setEditedChart(draft => {
      dotPropImmutable.get(draft, linesPath()).splice(p[3] as number, 1);
    });
    setActiveBeat(prev_path.join("."));
  }

  function breakLine() {
    const new_line_1 = cloneDeep(dotPropImmutable.get(editedChart, linePath()));
    const new_line_2 = cloneDeep(dotPropImmutable.get(editedChart, linePath()));
    new_line_1.measures = new_line_1.measures.slice(0, measureI());
    new_line_2.measures = new_line_2.measures.slice(measureI());
    setEditedChart(draft => {
      dotPropImmutable.get(draft, linesPath()).splice(lineI(), 1, new_line_1, new_line_2);
    });
  }

  function duplicateLine() {
    const new_line = cloneDeep(dotPropImmutable.get(editedChart, linePath()));
    setEditedChart(draft => {
      dotPropImmutable.get(draft, linesPath()).splice(lineI(), 0, new_line);
    });
  }

  function addSection() {
    const new_section = new ChartMaker().newChordSection();
    setEditedChart(draft => {
      draft.chords.splice(sectionI() + 1, 0, new_section);
    });
  }

  function removeSection() {
    const prev_path = cloneDeep(parsedPath());
    prev_path[1] = Math.max(prev_path[1] as number - 1, 0);
    setEditedChart(draft => {
      draft.chords.splice(sectionI(), 1);
    });
    setActiveBeat(prev_path.join("."));
  }

  function breakSection() {
    const new_section_1 = cloneDeep(dotPropImmutable.get(editedChart, sectionPath()));
    const new_section_2 = cloneDeep(dotPropImmutable.get(editedChart, sectionPath()));
    new_section_1.lines = new_section_1.lines.slice(0, lineI());
    new_section_2.lines = new_section_2.lines.slice(lineI());
    setEditedChart(draft => {
      dotPropImmutable.get(draft, sectionsPath()).splice(sectionI(), 1, new_section_1, new_section_2);
    });
  }

  function duplicateSection() {
    const section = cloneDeep(dotPropImmutable.get(editedChart, sectionPath()));
    setEditedChart(draft => {
      dotPropImmutable.get(draft, sectionsPath()).splice(sectionI() + 1, 0, section);
    });
  }

  function nudgeSection(dir: "up" | "down") {
    const i = sectionI();
    const { j, warning } = NudgeIndexer.parse(dir, i, editedChart.chords.length);

    if (warning) {
      notify(warning, "warning");
      return;
    }

    setEditedChart(draft => {
      [draft.chords[i], draft.chords[j]] = [draft.chords[j], draft.chords[i]];
    });
    const prev_path = cloneDeep(parsedPath());
    prev_path[1] = j;
    setActiveBeat(prev_path.join("."));
  }

  function addOneBeatPerMeasure() {
    const bi = parseInt(beatI() as string);
    setEditedChart(draft => {
      draft.chords.forEach((section) => {
        section.lines.forEach((line) => {
          line.measures.forEach((measure) => {
            measure.beats.splice(bi + 1, 0, "");
          });
        });
      });
    });
  }

  function removeOneBeatPerMeasure() {
    const bi = beatI() as number;
    setEditedChart(draft => {
      draft.chords.forEach((section) => {
        section.lines.forEach((line) => {
          line.measures.forEach((measure) => {
            measure.beats.splice(bi, 1);
          });
        });
      });
    });
  }

  const editLocationIsPresent = () => !!edit_location;
  const editLocationIsOnABeat = () => (edit_location ?? "").includes("beats");
  const editLocationIsOnAMeasure = () => (edit_location ?? "").includes("measures");
  const editLocationIsOnALine = () => (edit_location ?? "").includes("lines");

  function addRemove(
    add: () => void,
    remove: () => void,
    isEnabled: () => boolean = editLocationIsPresent,
    add_text = "add",
    remove_text = "remove"
  ) {
    return [
      <button
        key={add_text}
        data-tooltip-content={add_text}
        data-tooltip-id="light-tooltip"
        disabled={!isEnabled()}
        className={btnClass}
        onClick={add}
      >
        <FaPlus />
      </button>,
      <button
        key={remove_text}
        data-tooltip-content={remove_text}
        data-tooltip-id="light-tooltip"
        disabled={!isEnabled()}
        className={btnClass}
        onClick={remove}
      >
        <FaMinus />
      </button>,
    ];
  }

  return (
    <div className="chart-edit-buttons flex flex-wrap items-center gap-3">
      <span className="flex items-center gap-1">
        <span className="text-xs text-gray-400"> beat per measure: </span>
        {addRemove(
          addOneBeatPerMeasure,
          removeOneBeatPerMeasure,
          undefined,
          "add one beat to all measures after current location index",
          "remove one beat from all measures at current location index"
        )}
      </span>

      <span className="flex items-center gap-1">
        <span className="text-xs text-gray-400">beat: </span>
        {addRemove(addBeat, removeBeat, editLocationIsOnABeat)}
      </span>

      <span className="flex items-center gap-1">
        <span className="text-xs text-gray-400">measure: </span>
        {addRemove(addMeasure, removeMeasure, editLocationIsOnAMeasure)}
      </span>

      <span className="flex items-center gap-1">
        <span className="text-xs text-gray-400">line: </span>
        {addRemove(addLine, removeLine, editLocationIsOnALine)}

        <button
          disabled={!edit_location}
          data-tooltip-content="break line at beginning of measure (ctl+x)"
          data-tooltip-id="light-tooltip"
          className={btnClass}
          onClick={breakLine}
        >
          <FaScissors />
        </button>

        <button
          disabled={!edit_location}
          data-tooltip-content="duplicate line below (ctl+d)"
          data-tooltip-id="light-tooltip"
          className={btnClass}
          onClick={duplicateLine}
        >
          <FaCopy />
        </button>
      </span>

      <span className="flex items-center gap-1">
        <span className="text-xs text-gray-400">section: </span>
        {addRemove(addSection, removeSection)}
        <button
          disabled={!edit_location}
          data-tooltip-content="start a new section, break at beginning of line"
          data-tooltip-id="light-tooltip"
          className={btnClass}
          onClick={breakSection}
        >
          <FaScissors />
        </button>

        <button
          disabled={!edit_location}
          data-tooltip-content="duplicate this section"
          data-tooltip-id="light-tooltip"
          className={btnClass}
          onClick={duplicateSection}
        >
          <FaCopy />
        </button>

        <button
          disabled={!edit_location}
          data-tooltip-content="move section up"
          data-tooltip-id="light-tooltip"
          className={btnClass}
          onClick={() => nudgeSection("up")}
        >
          <FaArrowUp />
        </button>

        <button
          disabled={!edit_location}
          data-tooltip-content="move section down"
          data-tooltip-id="light-tooltip"
          className={btnClass}
          onClick={() => nudgeSection("down")}
        >
          <FaArrowDown />
        </button>
      </span>

      <span className="flex items-center gap-1">
        <span className="text-xs text-gray-400">go: </span>
        <button
          disabled={historyIsAtStart()}
          data-tooltip-content="undo"
          data-tooltip-id="light-tooltip"
          className={btnClass}
          onClick={() => historyGo("back")}
        >
          <FaRotateLeft className='inline' /> {historyNumItems("back")}
        </button>

        <button
          data-tooltip-content="redo"
          data-tooltip-id="light-tooltip"
          disabled={historyIsAtEnd()}
          className={btnClass}
          onClick={() => historyGo("forward")}
        >
          <FaRotateRight className='inline' /> {historyNumItems("forward")}
        </button>
      </span>

      {!edit_location && (
        <span className="opacity-50 text-orange-400 text-xs">
          click in edit area to activate buttons
        </span>
      )}
    </div>
  );
}
